/**
 * Student Hero Card
 * Displays XP, Level, Progress Bar, and Next Level Perks
 */

import type { StudentStats, Guild } from '@/types/tisa-coins';

interface LevelInfo {
  currentLevel: number;
  nextLevel: number;
  currentLevelXP: number;
  nextLevelXP: number;
  xpToNextLevel: number;
  perks: string[];
  progress: number;
}

interface StudentHeroCardProps {
  stats: StudentStats;
  levelInfo: LevelInfo;
  guild: Guild | null;
}

export function StudentHeroCard({ stats, levelInfo, guild }: StudentHeroCardProps) {
  const progressPercent = (levelInfo.progress * 100).toFixed(1);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900/50 to-gray-900/50 border-2 border-gold/30 p-8 mb-8">
      {/* Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-purple/10 to-gold/10 opacity-30 animate-pulse"></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          {/* Level Badge */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gold to-amber-600 flex items-center justify-center border-4 border-gold/50 shadow-2xl">
                <span className="text-4xl font-orbitron font-black text-white">
                  {stats.current_level}
                </span>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-purple text-white px-3 py-1 rounded-full text-xs font-bold border-2 border-white/30">
                LVL {stats.current_level}
              </div>
            </div>

            {/* XP Stats */}
            <div>
              <h2 className="text-4xl font-cinzel font-black text-white mb-2 drop-shadow-lg">
                Level {stats.current_level}
              </h2>
              <p className="text-xl text-gray-100">
                <span className="text-emerald-300 font-orbitron font-black">{stats.total_xp} XP</span>
                {' '}/{' '}
                <span className="text-gray-300 font-semibold">{levelInfo.nextLevelXP} XP</span>
              </p>
              <p className="text-base text-yellow-200 mt-2 font-semibold">
                🎯 {levelInfo.xpToNextLevel} XP to Level {levelInfo.nextLevel}
              </p>
            </div>
          </div>

          {/* Guild Badge */}
          {guild && (
            <div className={`px-6 py-4 rounded-xl bg-gradient-to-br ${guild.color} bg-opacity-30 border-2 border-cyan-300/50 text-center shadow-xl`}>
              <div className="text-5xl mb-2 drop-shadow-lg">{guild.icon}</div>
              <p className="font-cinzel font-black text-white text-xl drop-shadow-md">{guild.name}</p>
              <p className="text-sm text-cyan-100 italic mt-2 font-medium">"{guild.motto}"</p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-semibold text-gray-200 font-poppins">
              ⚡ Progress to Level {levelInfo.nextLevel}
            </span>
            <span className="text-lg text-emerald-300 font-black bg-emerald-500/20 px-3 py-1 rounded-full border border-emerald-400/50">
              {progressPercent}%
            </span>
          </div>
          <div className="h-5 bg-gray-800 rounded-full overflow-hidden border-2 border-gray-600 shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 transition-all duration-500 ease-out relative shadow-lg"
              style={{ width: `${progressPercent}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Next Level Perks */}
        {levelInfo.perks && levelInfo.perks.length > 0 && (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-5 border-2 border-purple-400/40">
            <p className="text-base font-semibold text-yellow-300 mb-3 flex items-center gap-2">
              🎁 <span>Unlock at Level {levelInfo.nextLevel}:</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {levelInfo.perks.map((perk: string, i: number) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-gradient-to-r from-purple-400 to-pink-400 text-white font-semibold border-2 border-white/30 rounded-full text-sm shadow-lg"
                >
                  ✨ {perk}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
